/*
 * Copyright © 2024 Himitsu Lab Limited. All Rights Reserved.
 */

import React from 'react'
import { defaultAvatar } from './room/audio_conference/CustomAudioParticipantPlaceholder'
import { UserType } from './livekitTypes'
import {
  useMeetingEndTimerHook,
  useReceiveAudioRecordingStatus,
  useReceiveRecordingStatus,
  useSendAudioRecordingStatus,
  useSendRecordingStatus,
} from './livekitHooks'
import { TrackLoop, TrackReferenceOrPlaceholder } from '@livekit/components-react'
import PermissionsToasterComp from './room/PermissionsToasterComp'
import { useTranslation } from 'react-i18next'

/**
 * A React component that displays a minimized representation of a meeting, including the meeting title, profile image, and video tracks.
 * It also handles displaying alerts when the meeting is about to end.
 *
 * @param {string} meetingTitle - The title of the meeting.
 * @param {string} profileImg - The URL of the profile image.
 * @param {TrackReferenceOrPlaceholder[]} tracks - An array of track references or placeholders.
 * @param {(type: string, message: string) => void} [onMessage] - An optional callback function to handle messages.
 * @return {JSX.Element} The rendered minimized meeting component.
 */

export default function MinimizedComp({
  meetingTitle,
  profileImg,
  tracks,
  onMessage,
}: {
  meetingTitle: string
  profileImg: string
  tracks: TrackReferenceOrPlaceholder[]
  onMessage?: (type: string, message: string) => void
}) {
  const [isNullPresent, setIsNullPresent] = React.useState(false)
  let metaData: any
  const { showAlertFiveMin, twoMinAlert, showPing } = useMeetingEndTimerHook()
  const { t } = useTranslation()

  useSendRecordingStatus()
  useReceiveRecordingStatus()
  useSendAudioRecordingStatus()
  useReceiveAudioRecordingStatus()

  React.useEffect(() => {
    if (profileImg && (profileImg.includes('null') || profileImg.includes('undefined'))) {
      setIsNullPresent(true)
    }
  }, [profileImg])

  if (showAlertFiveMin && onMessage) {
    onMessage(`${t('meetingWillEndIn')} 5 ${t('minutes')}`, 'error')
  }

  if (twoMinAlert && onMessage) {
    onMessage(`${t('meetingWillEndIn')} 2 ${t('minutes')}`, 'error')
  }

  return (
    <>
      <TrackLoop tracks={tracks}>
        <PermissionsToasterComp />
      </TrackLoop>
      <div className="inline-block bg-white relative rounded-xl w-full">
        <div className="w-20 h-20 rounded-full bg-white z-50 absolute -top-1 -left-10">
          {isNullPresent ? (
            <div className="bg-gray-200 flex w-20 h-20 justify-center items-center rounded-full">
              <img
                className={` rounded-full h-full w-full ${
                  metaData?.userType === UserType.HOST ? 'outline outline-yellow-400' : ''
                } ${metaData?.userType === UserType.CO_HOST ? 'outline outline-orange-400' : ''}`}
                src={defaultAvatar}
                alt="Avatar"
              />
            </div>
          ) : (
            <img
              className={` rounded-full h-full w-full ${
                metaData?.userType === UserType.HOST ? 'outline outline-yellow-400' : ''
              } ${metaData?.userType === UserType.CO_HOST ? 'outline outline-orange-400' : ''}`}
              src={profileImg}
              alt="img"
            />
          )}
        </div>
        <div className="flex pl-12 pr-2 h-full w-full justify-center items-center py-1">
          <div className="flex flex-col gap-2 w-full justify-start ">
            <div className="flex w-full gap-2 justify-center items-center">
              <div
                id="PIP-btn_animatePing"
                className={`${showPing ? 'animate-ping w-3 h-3' : 'w-3 h-3'} rounded-full bg-red-600`}
              />
              <div id="PIP-meetingTitle" className="text-base font-normal w-full truncate text-black">
                {meetingTitle}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
